import { Fragment, useEffect, useState } from 'react';
import { api, formatUsd, formatUptime, type CostPoint, type LiveCosts } from '../lib/api';
import { Card, StatTile, Alert, Spinner, Button, Empty } from '../components/ui';
import { CostChart, BreakdownBars } from '../components/CostChart';

type CostView = 'machines' | 'medialive' | 'mediaconnect' | 'encoders';

interface CostPart {
  id: string;
  label: string;
  kind: string;
  usd: number;
  daily: number[];
}

interface CostGroup {
  id: string;
  name: string;
  subtitle: string;
  state: 'running' | 'idle' | 'stopped' | 'gone' | null;
  usd: number;
  daily: number[];
  parts: CostPart[];
  usdPerHour?: number | null;
}

interface ResourceCosts {
  days: string[];
  groups: CostGroup[];
  total: number;
  historyFrom: string | null;
}

const VIEWS: { id: CostView; label: string; note: string }[] = [
  {
    id: 'machines',
    label: 'Instances',
    note:
      'Each instance with everything that bills because of it - compute while running, and its disks and ' +
      'public addresses whether it is running or not. A stopped instance still pays for its disks.',
  },
  {
    id: 'medialive',
    label: 'MediaLive',
    note:
      'Each channel with the inputs attached to it. Idle channels and detached inputs still bill - ' +
      'delete what is not coming back.',
  },
  {
    id: 'encoders',
    label: 'Encoders',
    note:
      'Each encoder launched from the dashboard, with everything it has had - channel, inputs, flows and RTMP ' +
      'encodes - as one line. Deleted encoders stay, so a season can be added up after the event.',
  },
  {
    id: 'mediaconnect',
    label: 'MediaConnect',
    note:
      'A flow bills by the hour while it is active, whether or not anything is watching it; standby ' +
      'flows cost nothing.',
  },
];

/**
 * One row's spend per day, as a strip of thin bars.
 *
 * Scaled to the busiest day of any row on the tab, not per row, so a tall bar
 * means the same money wherever it appears and rows compare at a glance. A day
 * with nothing billed is a baseline tick, not a gap, so the strip still reads
 * as a calendar.
 */
function DailyStrip({ daily, days, max }: { daily: number[]; days: string[]; max: number }) {
  return (
    <div className="flex h-6 w-24 items-end sm:w-40" style={{ gap: days.length > 30 ? 1 : 2 }}>
      {daily.map((usd, i) => (
        <div
          key={days[i]}
          className="flex-1"
          title={`${days[i]}: ${formatUsd(usd)}`}
          style={{
            height: usd > 0.005 ? `${Math.max(8, (usd / max) * 100)}%` : '1px',
            background: usd > 0.005 ? 'var(--color-series-1)' : 'var(--color-hairline)',
            borderRadius: usd > 0.005 ? '2px 2px 0 0' : 0,
          }}
        />
      ))}
    </div>
  );
}

/**
 * What state a row is in, as words beside a symbol - never colour alone.
 * Amber for "billing by the hour now" and "billing while unused", because
 * both are money going out that someone may not know about.
 */
function StateLabel({ group }: { group: CostGroup }) {
  const amber = { color: 'var(--color-warning)' };
  switch (group.state) {
    case 'running':
      return <span style={amber}>● running now</span>;
    case 'idle':
      return <span style={amber}>◐ billing while unused</span>;
    case 'gone':
      return <span className="text-ink-muted">✕ no longer exists</span>;
    default:
      return null;
  }
}

function ResourceCostsCard() {
  const [view, setView] = useState<CostView>(() => {
    try {
      const saved = localStorage.getItem('cloudprod-cost-view');
      return saved === 'medialive' || saved === 'mediaconnect' || saved === 'encoders' ? saved : 'machines';
    } catch {
      return 'machines';
    }
  });
  const [days, setDays] = useState(14);
  const [data, setData] = useState<ResourceCosts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    api
      .get<ResourceCosts>(`/costs/resources?view=${view}&days=${days}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err: Error) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [view, days]);

  const chooseView = (v: CostView) => {
    setView(v);
    setOpen(new Set());
    try { localStorage.setItem('cloudprod-cost-view', v); } catch { /* per-viewer nicety only */ }
  };
  const toggle = (id: string) =>
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const max = Math.max(0.01, ...(data?.groups.flatMap((g) => g.daily) ?? [0]));
  // Honest about how far back the per-resource numbers go: Cost Explorer
  // keeps 14 days of them, and older days are only what has been saved since.
  const partial = data?.historyFrom && data.days[0] < data.historyFrom;
  const running = data?.groups.filter((g) => g.state === 'running') ?? [];
  const idle = data?.groups.filter((g) => g.state === 'idle') ?? [];
  const idleUsd = idle.reduce((s, g) => s + g.usd, 0);

  return (
    <Card
      title={`Costs by resource — last ${days} days`}
      action={
        <div className="flex gap-1">
          {[7, 14, 30, 90].map((d) => (
            <Button key={d} variant={d === days ? 'secondary' : 'ghost'} onClick={() => setDays(d)}>
              {d}d
            </Button>
          ))}
        </div>
      }
    >
      <div className="flex gap-1 overflow-x-auto border-b border-grid px-4 pt-2 [scrollbar-width:none]">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            onClick={() => chooseView(v.id)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
              v.id === view ? 'border-series-1 text-ink' : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="p-4"><Alert tone="warning">{error}</Alert></div>
      ) : !data ? (
        <div className="p-4"><Spinner label="Loading costs…" /></div>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-b border-grid px-4 py-3 text-sm">
            <span>
              <strong className="tabular">{formatUsd(data.total)}</strong>
              <span className="text-ink-muted">
                {' '}across {data.groups.length}{' '}
                {view === 'machines' ? 'instances and groups' : view === 'medialive' ? 'channels and inputs' : view === 'encoders' ? 'encoders' : 'flows'}
              </span>
            </span>
            {running.length > 0 && (
              <span style={{ color: 'var(--color-warning)' }}>● {running.length} running now</span>
            )}
            {idle.length > 0 && (
              <span style={{ color: 'var(--color-warning)' }}>
                ◐ {formatUsd(idleUsd)} billing while unused
              </span>
            )}
          </div>

          <p className="px-4 pt-3 text-xs text-ink-muted">
            {VIEWS.find((v) => v.id === view)?.note}
            {partial &&
              ` Per-resource figures start ${data.historyFrom}: Cost Explorer keeps 14 days of them, and this ` +
                'dashboard saves each day from then on, so longer ranges fill in over time.'}
          </p>

          {data.groups.length === 0 ? (
            <Empty title="Nothing billed">Nothing here cost anything in this period.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-grid text-left text-xs text-ink-muted">
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Per day</th>
                    <th className="px-4 py-2 text-right font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.groups.map((g) => {
                    const expandable = g.parts.length > 1;
                    const isOpen = open.has(g.id);
                    return (
                      <Fragment key={g.id}>
                        <tr
                          className={`border-b border-grid ${expandable ? 'cursor-pointer hover:bg-raised/40' : ''}`}
                          onClick={() => expandable && toggle(g.id)}
                        >
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2 font-medium">
                              {expandable && (
                                <span className="w-3 text-xs text-ink-muted">{isOpen ? '▾' : '▸'}</span>
                              )}
                              <span className={expandable ? '' : 'pl-5'}>{g.name}</span>
                            </div>
                            <div className="pl-5 text-xs text-ink-muted">
                              <StateLabel group={g} />
                              {g.state === 'running' || g.state === 'idle' || g.state === 'gone' ? ' · ' : ''}
                              {g.subtitle}
                              {g.usdPerHour ? ` · ${formatUsd(g.usdPerHour, 3)}/hr while active` : ''}
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <DailyStrip daily={g.daily} days={data.days} max={max} />
                          </td>
                          <td className="px-4 py-2 text-right tabular">{formatUsd(g.usd)}</td>
                        </tr>
                        {isOpen &&
                          g.parts.map((p) => (
                            <tr key={p.id} className="border-b border-grid bg-plane/40 text-xs">
                              <td className="py-1.5 pl-12 pr-4 text-ink-secondary">{p.label}</td>
                              <td className="px-4 py-1.5">
                                <DailyStrip daily={p.daily} days={data.days} max={max} />
                              </td>
                              <td className="px-4 py-1.5 text-right tabular text-ink-secondary">
                                {formatUsd(p.usd)}
                              </td>
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="px-4 pb-3 pt-2 text-xs text-ink-muted">
            From Cost Explorer's per-resource data, which lags up to a day - the newest bar is still
            filling in. Click a row with more than one part to see what it is made of.
          </p>
        </>
      )}
    </Card>
  );
}

interface Summary {
  live: LiveCosts;
  monthToDate: { total: number; byService: { key: string; amount: number }[] } | null;
  forecast: number | null;
  costExplorerError: string | null;
  byGroup: { groupId: string | null; usd: number; hours: number }[];
}

// ---- Recordings storage ---------------------------------------------------

interface OwnerStorage {
  id: string;
  name: string;
  kind: 'machine' | 'encoder' | 'flow' | 'unknown';
  bytes: number;
  objects: number;
  sessions: number;
  usdPerMonth: number;
}

interface RecordingsStorage {
  bucket: string | null;
  bytes: number;
  objects: number;
  usdPerMonth: number;
  pricePerGbMonth: number;
  retentionDays: number;
  owners: OwnerStorage[];
  computedAt: string | null;
  computing: boolean;
  error: string | null;
}

const gb = (bytes: number) => (bytes >= 1_073_741_824 * 100 ? `${(bytes / 1_073_741_824).toFixed(0)} GB`
  : bytes >= 1_073_741_824 ? `${(bytes / 1_073_741_824).toFixed(1)} GB`
  : `${(bytes / 1_048_576).toFixed(0)} MB`);

/**
 * What the recordings take up in S3 and what that costs a month, by the
 * machine, encoder or flow whose recordings they are. Summed on the hub
 * once an hour (a walk of the bucket), so the figure is as of `computedAt`.
 */
function StorageCard() {
  const [data, setData] = useState<RecordingsStorage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (refresh = false) => {
    setBusy(true);
    try {
      const d = await api.get<RecordingsStorage>(`/costs/storage${refresh ? '?refresh=true' : ''}`);
      setData(d);
      setError(d.error);
      // The first walk ever can take longer than the page waits: ask again shortly.
      if (d.computing) setTimeout(() => void load(), 10_000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const top = data?.owners.slice(0, 12) ?? [];
  const most = top[0]?.bytes ?? 1;
  const kindWord = { machine: 'instance', encoder: 'encoder', flow: 'flow', unknown: '' };

  return (
    <Card
      title="Recordings storage"
      action={
        <Button variant="ghost" disabled={busy} onClick={() => void load(true)} title="Walk the bucket again now (about a minute for a season of recordings)">
          {busy ? 'Adding up…' : 'Refresh'}
        </Button>
      }
    >
      <div className="space-y-4 p-4">
        {error && <Alert tone="warning">{error}</Alert>}
        {data?.computing && !data.computedAt ? (
          <Spinner label="Adding up the recordings bucket for the first time…" />
        ) : data ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile label="In S3 now" value={gb(data.bytes)} sub={`${data.objects.toLocaleString()} objects · ${data.owners.reduce((n, o) => n + o.sessions, 0)} recordings`} />
              <StatTile label="Storage a month" value={formatUsd(data.usdPerMonth)} sub={`At this size: S3 Standard, ${formatUsd(data.pricePerGbMonth, 3)} per GB-month`} />
              <StatTile label="Kept for" value={`${data.retentionDays} days`} sub="Then S3 deletes a recording by itself. Trim or delete sooner to pay less." />
            </div>
            {top.length > 0 && (
              <div className="divide-y divide-grid">
                {top.map((o) => (
                  <div key={o.id} className="flex items-center gap-3 py-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{o.name}</span>
                        {o.kind !== 'unknown' && <span className="shrink-0 text-xs text-ink-muted">{kindWord[o.kind]}</span>}
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded bg-plane">
                        <div className="h-full bg-series-1" style={{ width: `${Math.max(1, (o.bytes / most) * 100)}%` }} />
                      </div>
                    </div>
                    <div className="w-36 shrink-0 text-right text-xs tabular">
                      <div>{gb(o.bytes)}</div>
                      <div className="text-ink-muted">{formatUsd(o.usdPerMonth)} a month · {o.sessions} recording{o.sessions === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                ))}
                {data.owners.length > top.length && (
                  <div className="py-2 text-xs text-ink-muted">and {data.owners.length - top.length} more</div>
                )}
              </div>
            )}
            <p className="text-xs text-ink-muted">
              {data.computedAt ? `As of ${new Date(data.computedAt).toLocaleString()}. ` : ''}
              Added up from the bucket once an hour. Storage only: the few cents of requests and a share link's downloads are not counted.
              An owner with no recordings listed has files left behind, which Delete on the recordings page clears.
            </p>
          </>
        ) : (
          <Spinner label="Loading…" />
        )}
      </div>
    </Card>
  );
}

export function Costs() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [history, setHistory] = useState<CostPoint[]>([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      const [s, h] = await Promise.all([
        api.get<Summary>('/costs/summary').catch(() => null),
        api
          .get<{ points: CostPoint[] }>(`/costs/history?days=${days}&granularity=DAILY`)
          .catch((err: Error) => {
            setHistoryError(err.message);
            return { points: [] };
          }),
      ]);

      setSummary(s);
      setHistory(h.points);
      setLoading(false);
    }
    void load();
  }, [days]);

  if (loading) return <Spinner label="Loading costs…" />;

  const live = summary?.live;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold">Costs</h1>
        <p className="text-sm text-ink-muted">
          Live figures are estimated from observed running time. Cost Explorer is authoritative but
          lags up to 24 hours.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Burning now"
          value={`${formatUsd(live?.usdPerHourNow ?? 0, 2)}/hr`}
          sub={`${live?.runningNow ?? 0} instance${live?.runningNow === 1 ? '' : 's'} running`}
          tone={(live?.usdPerHourNow ?? 0) > 5 ? 'warning' : 'default'}
        />
        <StatTile label="Today" value={formatUsd(live?.todayUsd ?? 0)} sub="Estimated" />
        <StatTile label="This month" value={formatUsd(live?.monthUsd ?? 0)} sub="Estimated, our instances only" />
        <StatTile
          label="Account month to date"
          value={summary?.monthToDate ? formatUsd(summary.monthToDate.total) : '—'}
          sub={summary?.forecast ? `${formatUsd(summary.forecast)} forecast` : 'From Cost Explorer'}
        />
      </div>

      {summary?.costExplorerError && (
        <Alert tone="warning" title="Cost Explorer unavailable">
          {summary.costExplorerError}
        </Alert>
      )}

      {/* Daily account spend. One series, so the title names it and there is no
          legend — a legend for a single series is noise. */}
      <Card
        title={`Daily account spend — last ${days} days`}
        action={
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                variant={d === days ? 'secondary' : 'ghost'}
                onClick={() => {
                  setDays(d);
                  setLoading(true);
                }}
              >
                {d}d
              </Button>
            ))}
          </div>
        }
      >
        <div className="p-4">
          {historyError ? (
            <Alert tone="warning">{historyError}</Alert>
          ) : (
            <CostChart points={history} />
          )}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Spend by service — month to date">
          {summary?.monthToDate?.byService?.length ? (
            <BreakdownBars items={summary.monthToDate.byService} />
          ) : (
            <Empty title="No breakdown available" />
          )}
        </Card>

        <Card title="Running instances">
          {live?.byInstance.length ? (
            <div className="divide-y divide-grid">
              {live.byInstance.map((row) => (
                <div key={row.instanceId} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{row.name ?? row.instanceId}</div>
                    <div className="text-xs text-ink-muted">
                      {row.instanceType} · up {formatUptime(row.startedAt)}
                    </div>
                  </div>
                  <div className="text-right text-xs tabular">
                    <div>{formatUsd(row.sessionUsd)}</div>
                    <div className="text-ink-muted">
                      {formatUsd(row.usdPerHour, 2)}/hr
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty title="Nothing running">No compute charges are accruing right now.</Empty>
          )}
        </Card>
      </div>

      <ResourceCostsCard />

      <StorageCard />

      {summary && summary.byGroup.length > 0 && (
        <Card title="Spend by group — last 30 days">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grid text-left text-xs text-ink-muted">
                  <th className="px-4 py-2 font-medium">Group</th>
                  <th className="px-4 py-2 text-right font-medium">Hours</th>
                  <th className="px-4 py-2 text-right font-medium">Estimated cost</th>
                </tr>
              </thead>
              <tbody>
                {summary.byGroup.map((row) => (
                  <tr key={row.groupId ?? 'none'} className="border-b border-grid last:border-0">
                    <td className="px-4 py-2">{row.groupId ?? 'Ungrouped'}</td>
                    <td className="px-4 py-2 text-right tabular">{row.hours}</td>
                    <td className="px-4 py-2 text-right tabular">{formatUsd(row.usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
